// One requestAnimationFrame loop for every graph on the page.
//
// The homepage runs four simulations at once (hero + three Explore cards).
// Four independent rAF loops means four callbacks per frame, four layout
// thrashes, and a phone that gets warm on a marketing page. This is a single
// loop with a subscriber list: it starts when the first graph becomes visible
// and stops dead when the last one leaves — no idle loop burning battery on a
// page nobody is looking at.
//
// Subscribers receive elapsed seconds since the ticker started, so a card that
// scrolls into view joins the motion already in progress rather than snapping
// back to t=0.

const subscribers = new Set();
let frame = 0;
let startedAt = 0;

const loop = (now) => {
  const t = (now - startedAt) / 1000;
  // Copied before iterating: a subscriber may unsubscribe from inside its own
  // callback (an IntersectionObserver firing mid-frame), and mutating a Set
  // while iterating it silently skips entries.
  for (const fn of [...subscribers]) {
    try {
      fn(t);
    } catch (e) {
      // One broken graph must not take down the others — but say so, loudly.
      // Swallowing this silently once cost an afternoon: the canvas simply
      // stopped animating with no error anywhere.
      // eslint-disable-next-line no-console
      console.error("[graphTicker] subscriber threw, unsubscribing:", e);
      subscribers.delete(fn);
    }
  }
  if (subscribers.size) frame = requestAnimationFrame(loop);
  else frame = 0;
};

export const subscribe = (fn) => {
  subscribers.add(fn);
  if (!frame) {
    startedAt = performance.now();
    frame = requestAnimationFrame(loop);
  }
  return () => {
    subscribers.delete(fn);
    if (!subscribers.size && frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  };
};

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
